import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useBreakpoint } from '../hooks/useBreakpoint';

const C = { primary: '#8B1A1A', gold: '#D4A017', text: '#2C2C2C' };

const JEWELLERY_ITEMS = [
  { label: 'Earrings', to: '/shop?category=earrings' },
  { label: 'Necklaces', to: '/shop?category=necklaces' },
  { label: 'Bracelets', to: '/shop?category=bracelets' },
  { label: 'Rings', to: '/shop?category=rings' },
  { label: 'Potli Bags', to: '/shop?category=potli-bags' },
];

const NAV_LINKS = [
  { label: 'Punjabi Juttis', to: '/shop?category=juttis' },
  { label: 'Jewellery', jewellery: true },
  { label: 'Best Sellers', to: '/shop?is_best_seller=true' },
  { label: 'New Arrivals', to: '/shop?is_new_arrival=true' },
  { label: 'Under ₹499', to: '/shop?max_price=499' },
  { label: 'Under ₹999', to: '/shop?max_price=999' },
];

const MOBILE_LINKS = [
  { label: 'Punjabi Juttis', to: '/shop?category=juttis' },
  { label: 'Earrings', to: '/shop?category=earrings' },
  { label: 'Necklaces', to: '/shop?category=necklaces' },
  { label: 'Bracelets', to: '/shop?category=bracelets' },
  { label: 'Rings', to: '/shop?category=rings' },
  { label: 'Potli Bags', to: '/shop?category=potli-bags' },
  { label: 'Best Sellers', to: '/shop?is_best_seller=true' },
  { label: 'New Arrivals', to: '/shop?is_new_arrival=true' },
  { label: 'Under ₹499', to: '/shop?max_price=499' },
  { label: 'Under ₹999', to: '/shop?max_price=999' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const { count: wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const accountRef = useRef(null);
  const jewelleryRef = useRef(null);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [navigate]);

  useEffect(() => {
    const handler = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setOpenDropdown(d => d === 'account' ? null : d);
      }
      if (jewelleryRef.current && !jewelleryRef.current.contains(e.target)) {
        setOpenDropdown(d => d === 'jewellery' ? null : d);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setOpenDropdown(null);
    setMobileMenuOpen(false);
    navigate('/');
  };

  const badgeStyle = {
    position: 'absolute', top: -4, right: -6,
    background: C.gold, color: '#fff', borderRadius: '50%',
    width: 17, height: 17, fontSize: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700,
  };

  const iconBtn = {
    position: 'relative', background: 'none', border: 'none',
    cursor: 'pointer', fontSize: 20, color: C.primary, padding: 4,
  };

  const dropdownMenuStyle = {
    position: 'absolute', top: 'calc(100% + 8px)', left: 0,
    background: '#fff', borderRadius: 6,
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    minWidth: 170, overflow: 'hidden', zIndex: 300,
  };

  const dropdownItemStyle = {
    display: 'block', padding: '10px 16px', fontSize: 14,
    color: C.text, textDecoration: 'none', borderBottom: '1px solid #f0e0e0',
    cursor: 'pointer',
  };

  return (
    <>
      <nav style={{
        position: 'sticky', top: 0, zIndex: 200,
        background: '#fff', boxShadow: '0 2px 8px rgba(139,26,26,0.12)',
        padding: isMobile ? '0 16px' : '0 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: 60,
      }}>
        {/* Logo */}
        <Link to="/" style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 22 : 24, fontWeight: 700, color: C.primary, textDecoration: 'none', letterSpacing: 1, flexShrink: 0 }}>
          A1 Looks
        </Link>

        {/* Desktop nav links */}
        {!isMobile && (
          <ul style={{ display: 'flex', gap: 20, listStyle: 'none', alignItems: 'center', margin: '0 12px', padding: 0, flex: 1, justifyContent: 'center' }}>
            {NAV_LINKS.map(l => (
              l.jewellery ? (
                <li key="jewellery" style={{ position: 'relative' }} ref={jewelleryRef}>
                  <button
                    onClick={() => setOpenDropdown(o => o === 'jewellery' ? null : 'jewellery')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: C.text, fontWeight: 400, letterSpacing: 0.3, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    Jewellery <span style={{ fontSize: 9, color: '#999' }}>{openDropdown === 'jewellery' ? '▲' : '▼'}</span>
                  </button>
                  {openDropdown === 'jewellery' && (
                    <div style={dropdownMenuStyle}>
                      {JEWELLERY_ITEMS.map(item => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setOpenDropdown(null)}
                          style={dropdownItemStyle}
                          onMouseEnter={e => e.currentTarget.style.background = '#FFF8F0'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >{item.label}</Link>
                      ))}
                    </div>
                  )}
                </li>
              ) : (
                <li key={l.to}>
                  <Link to={l.to} style={{ textDecoration: 'none', color: C.text, fontSize: 14, fontWeight: 400, letterSpacing: 0.3, whiteSpace: 'nowrap' }}>
                    {l.label}
                  </Link>
                </li>
              )
            ))}
          </ul>
        )}

        {/* Right side icons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10, flexShrink: 0 }}>
          <button style={iconBtn} onClick={() => navigate('/wishlist')} title="Wishlist">
            ♡{wishlistCount > 0 && <span style={badgeStyle}>{wishlistCount}</span>}
          </button>
          <button style={iconBtn} onClick={() => navigate('/cart')} title="Cart">
            🛒{cartCount > 0 && <span style={badgeStyle}>{cartCount}</span>}
          </button>

          {/* Desktop auth */}
          {!isMobile && (
            user ? (
              <div style={{ position: 'relative' }} ref={accountRef}>
                <button
                  onClick={() => setOpenDropdown(o => o === 'account' ? null : 'account')}
                  style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 4, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  👤 {user.first_name || user.username} <span style={{ fontSize: 10 }}>{openDropdown === 'account' ? '▲' : '▼'}</span>
                </button>
                {openDropdown === 'account' && (
                  <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', background: '#fff', borderRadius: 6, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 180, overflow: 'hidden', zIndex: 300 }}>
                    {[{ label: '👤 My Profile', path: '/profile' }, { label: '📦 My Orders', path: '/my-orders' }, { label: '♡ Wishlist', path: '/wishlist' }].map(item => (
                      <div key={item.path} onClick={() => { navigate(item.path); setOpenDropdown(null); }}
                        style={{ padding: '11px 18px', fontSize: 14, color: C.text, cursor: 'pointer', borderBottom: '1px solid #f0e0e0' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#FFF8F0'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      >{item.label}</div>
                    ))}
                    <div onClick={handleLogout} style={{ padding: '11px 18px', fontSize: 14, color: '#e53935', cursor: 'pointer', fontWeight: 600 }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fff5f5'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >Sign Out</div>
                  </div>
                )}
              </div>
            ) : (
              <button onClick={() => navigate('/login')} style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 4, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Login
              </button>
            )
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <button
              onClick={() => setMobileMenuOpen(o => !o)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', flexDirection: 'column', gap: 5, width: 28 }}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <span style={{ display: 'block', width: '100%', height: 2, background: mobileMenuOpen ? 'transparent' : C.primary, transition: 'all 0.2s' }} />
              <span style={{ display: 'block', width: '100%', height: 2, background: C.primary, transform: mobileMenuOpen ? 'rotate(45deg) translate(5px, 0)' : 'none', transition: 'all 0.2s', transformOrigin: 'left' }} />
              <span style={{ display: 'block', width: '100%', height: 2, background: C.primary, transform: mobileMenuOpen ? 'rotate(-45deg) translate(5px, 0)' : 'none', transition: 'all 0.2s', transformOrigin: 'left' }} />
            </button>
          )}
        </div>
      </nav>

      {/* Mobile drawer */}
      {isMobile && (
        <>
          {mobileMenuOpen && (
            <div
              onClick={() => setMobileMenuOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 150 }}
            />
          )}
          <div style={{
            position: 'fixed', top: 60, left: 0, right: 0,
            background: '#fff', zIndex: 190,
            transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(-110%)',
            transition: 'transform 0.28s ease',
            boxShadow: mobileMenuOpen ? '0 8px 24px rgba(0,0,0,0.12)' : 'none',
            maxHeight: 'calc(100vh - 60px)',
            overflowY: 'auto',
          }}>
            {MOBILE_LINKS.map(l => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileMenuOpen(false)}
                style={{ display: 'block', padding: '14px 20px', textDecoration: 'none', color: C.text, fontSize: 15, borderBottom: '1px solid #f0e0e0', fontWeight: 500 }}
              >
                {l.label}
              </Link>
            ))}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0e0e0' }}>
              {user ? (
                <div>
                  <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Signed in as <strong>{user.first_name || user.username}</strong></p>
                  {[{ label: '👤 My Profile', path: '/profile' }, { label: '📦 My Orders', path: '/my-orders' }].map(item => (
                    <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                      style={{ display: 'block', padding: '10px 0', textDecoration: 'none', color: C.text, fontSize: 15 }}
                    >{item.label}</Link>
                  ))}
                  <button onClick={handleLogout} style={{ marginTop: 12, background: 'none', border: '1.5px solid #e53935', color: '#e53935', padding: '10px 20px', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%' }}>
                    Sign Out
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 12 }}>
                  <button onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} style={{ flex: 1, padding: '12px', background: C.primary, color: '#fff', border: 'none', borderRadius: 4, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                    Login
                  </button>
                  <button onClick={() => { navigate('/register'); setMobileMenuOpen(false); }} style={{ flex: 1, padding: '12px', background: '#fff', color: C.primary, border: `2px solid ${C.primary}`, borderRadius: 4, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                    Register
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
